import { OpenAPIV3_1 as OpenApi } from 'openapi-types';
import { DocsLinks } from '~/constants';
import { ExternalApiEndpoint } from '~/queries';
import { slugify } from '~/utils/stringHelper';

const sortIndexMap = [
  'get',
  'list',
  'create',
  'update',
  'delete',
  'add',
  'remove',
  'start',
  'stop',
  'retry',
];

export const getApiServer = () =>
  import.meta.env.VITE_EXTERNAL_API_BASE_URL || `${window.location.origin}/api/v1`;

export const getEndpointBadgeOption = (action: string) => {
  switch (action) {
    case 'get':
      return { label: 'GET', variant: 'blue' as const };
    case 'put':
      return { label: 'PUT', variant: 'teal' as const };
    case 'post':
      return { label: 'POST', variant: 'green' as const };
    case 'delete':
      return { label: 'DELETE', variant: 'warning' as const };
    default:
      return { label: action, variant: 'primary' as const };
  }
};

export const getIsIntegrationEndpoint = (endpoint: ExternalApiEndpoint) =>
  endpoint.path === '/workspaces/{workspace_id}/processes/{process_id}/process-runs-integrations';

export const getEndpointLink = (endpoint: ExternalApiEndpoint) => {
  if (!endpoint.summary) return null;
  const section = endpoint.tags?.[0] ? `/${slugify(endpoint.tags[0])}` : '';
  return `${DocsLinks.API}${section}/${slugify(endpoint.summary)}`;
};

/**
 * Maps predefined API options to be used in a `Select` component
 */
export const getEndpointsOptions = (endpoints: Record<string, ExternalApiEndpoint>) =>
  Object.values(endpoints)
    .map((path) => ({
      optgroup: slugify(path.tags?.[0] ?? 'unknown'),
      label: path.summary || path.operationId,
      value: path.operationId,
      badge: getEndpointBadgeOption(path.method),
    }))
    .sort((left, right) => left.optgroup.localeCompare(right.optgroup))
    .sort((left, right) => {
      if (left.optgroup !== right.optgroup) return 0;

      const labelLeft: string = left.label?.toLowerCase() || '';
      const labelRight: string = right.label?.toLowerCase() || '';

      const indexLeft: number = sortIndexMap.findIndex((item) => labelLeft.startsWith(item)) || 0;
      const indexRight: number = sortIndexMap.findIndex((item) => labelRight.startsWith(item)) || 0;

      return indexLeft - indexRight;
    });

export const getEndpointParameters = (parameters: ExternalApiEndpoint['parameters'] = []) =>
  parameters.reduce<Record<'path' | 'query', Record<string, OpenApi.ParameterObject>>>(
    (acc, parameter) => {
      if (!('in' in parameter)) return acc;
      if (parameter.name === 'workspace_id') return acc;
      const type = parameter.in as 'path' | 'query';

      if (!acc[type]) {
        acc[type] = {};
      }

      if (parameter.in === 'path') {
        acc[type][parameter.name] = parameter;
      }

      if (parameter.in === 'query') {
        acc[type][parameter.name] = parameter;
      }
      return acc;
    },
    { path: {}, query: {} },
  );
